"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Download, Calendar, TrendingUp, Users, Award } from "lucide-react"
import type { Statistics } from "../types"
import { api } from "../services/api"
import { getWeekRange, getMonthRange, formatDate } from "../utils/dateUtils"
import { exportStatisticsToExcel } from "../utils/exportUtils"
import { BarChart3 } from "lucide-react"

export const StatisticsPage: React.FC = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [dateRange, setDateRange] = useState(() => getWeekRange(new Date()))
  const [rangeType, setRangeType] = useState<"week" | "month" | "custom">("week")
  const [customStart, setCustomStart] = useState(formatDate(new Date()))
  const [customEnd, setCustomEnd] = useState(formatDate(new Date()))
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStatistics()
  }, [dateRange])

  useEffect(() => {
    updateDateRange()
  }, [rangeType, customStart, customEnd])

  const updateDateRange = () => {
    const now = new Date()
    switch (rangeType) {
      case "week":
        setDateRange(getWeekRange(now))
        break
      case "month":
        setDateRange(getMonthRange(now))
        break
      case "custom":
        setDateRange({ start: customStart, end: customEnd })
        break
    }
  }

  const loadStatistics = async () => {
    try {
      setLoading(true)
      const data = await api.getStatistics(dateRange)
      setStatistics(data)
    } catch (error) {
      console.error("Error loading statistics:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    if (statistics) {
      exportStatisticsToExcel(statistics, dateRange)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading statistics...</span>
      </div>
    )
  }

  if (!statistics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No data available</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your date range.</p>
      </div>
    )
  }

  const totalSessions =
    statistics.trainingAttendance.reduce((sum, item) => sum + item.count, 0) +
    statistics.privateLessons.reduce((sum, item) => sum + item.count, 0)

  return (
    <div className="animate-fade-in">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Statistics</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">View attendance and performance statistics</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={handleExport}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md"
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Date Range Controls */}
      <div className="mt-6 card p-6 animate-slide-down">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="range-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Period
            </label>
            <select
              id="range-type"
              value={rangeType}
              onChange={(e) => setRangeType(e.target.value as "week" | "month" | "custom")}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {rangeType === "custom" && (
            <>
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  id="start-date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                />
              </div>

              <div>
                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  id="end-date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                />
              </div>
            </>
          )}

          <div className="flex items-end">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <span className="font-medium">
                  {dateRange.start} to {dateRange.end}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 animate-slide-up">
        <div className="card p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-blue-500 dark:text-blue-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Fencers</dt>
                <dd className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.totalFencers}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-green-500 dark:text-green-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Training Sessions</dt>
                <dd className="text-2xl font-bold text-gray-900 dark:text-white">
                  {statistics.trainingAttendance.reduce((sum, item) => sum + item.count, 0)}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Award className="h-8 w-8 text-purple-500 dark:text-purple-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Private Lessons</dt>
                <dd className="text-2xl font-bold text-gray-900 dark:text-white">
                  {statistics.privateLessons.reduce((sum, item) => sum + item.count, 0)}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-orange-500 dark:text-orange-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Sessions</dt>
                <dd className="text-2xl font-bold text-gray-900 dark:text-white">{totalSessions}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Fencer Statistics Table */}
      <div className="mt-8 card animate-slide-up">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Individual Fencer Statistics</h3>
        </div>
        <div className="overflow-hidden">
          <div className="responsive-table">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Fencer
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Training Sessions
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Private Lessons
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Total Sessions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {statistics.fencerStats.map((stat) => (
                  <tr
                    key={stat.fencerId}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {stat.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        {stat.trainingCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                        {stat.privateLessonCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {stat.trainingCount + stat.privateLessonCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {statistics.fencerStats.length === 0 && (
              <div className="text-center py-12">
                <BarChart3 className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No statistics available</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No data found for the selected time period.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
